import React, { useEffect, useState, useRef } from "react";
import {
  Box, Card, Grid, Typography, MenuItem, Select, FormControl, InputLabel,
  Button, Snackbar, Alert
} from "@mui/material";
import InfoIcon from "@mui/icons-material/Info";
import DescriptionIcon from "@mui/icons-material/Description";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CategoryIcon from "@mui/icons-material/Category";
import PendingActionsIcon from "@mui/icons-material/PendingActions";
import axios from "axios";
import EditArchitecturalRequest from "../EditArchitecturalRequests";
import api, { API_BASE_URL } from "../../../utils/api";
import RequestFilesViewer from "./RequestFilesViewer"; 


const RequestDetailsPanel = ({ requestDetails, onUpdate }) => {
  const [types, setTypes] = useState([]);
  const [subtypes, setSubtypes] = useState([]);
  const [priorities, setPriorities] = useState([]);
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState("");
  const [loadingTypes, setLoadingTypes] = useState(true);
  const [loadingSubtypes, setLoadingSubtypes] = useState(false);
  const [loadingPriorities, setLoadingPriorities] = useState(true);

  const [selectedType, setSelectedType] = useState("");
  const [selectedSubtype, setSelectedSubtype] = useState("");
  const [selectedPriority, setSelectedPriority] = useState("");

  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

  const editRequestRef = useRef();

  // Fetch request types
  useEffect(() => {
    const fetchTypes = async () => {
      try {
        const res = await api.get(`/architectural-request-types`);
        setTypes(res.data);
      } catch (err) {
        console.error("Failed to load types", err);
      } finally {
        setLoadingTypes(false);
      }
    };
    fetchTypes();
  }, []);

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const res = await api.get(`/users-by-role`);
        setUsers(res.data);
      } catch (err) {
        console.error("Failed to fetch users", err);
      }
    };
    fetchUsers();
  }, []);

  
  // Fetch priority levels
  useEffect(() => {
    const fetchPriorities = async () => {
      try {
        const res = await api.get(`/priority-levels`);
        setPriorities(res.data);
      } catch (err) {
        console.error("Failed to load priorities", err);
      } finally {
        setLoadingPriorities(false);
      }
    };
    fetchPriorities();
  }, []);

  // Set selectedType from requestDetails
  useEffect(() => {
    if (Array.isArray(requestDetails?.request_type) && types.length > 0) {
      const [typeId] = requestDetails.request_type;
      const numericTypeId = Number(typeId);
      const match = types.find(t => t.id === numericTypeId);
      if (match) {
        setSelectedType(numericTypeId);
      }
    }
  }, [types, requestDetails]);


  useEffect(() => {
    if (requestDetails?.assigned_to) {
      setSelectedUser(requestDetails.assigned_to);
    }
  }, [requestDetails]);
  

  // Fetch subtypes based on selectedType
  useEffect(() => {
    if (!selectedType) return;
    const fetchSubtypes = async () => {
      setLoadingSubtypes(true);
      try {
        const res = await api.get(`/architectural-request-subtypes?typeId=${selectedType}`);
        setSubtypes(res.data);
      } catch (err) {
        console.error("Failed to load subtypes", err);
      } finally {
        setLoadingSubtypes(false);
      }
    };
    fetchSubtypes();
  }, [selectedType]);

  // Set selectedSubtype from requestDetails
  useEffect(() => {
    if (requestDetails?.subtype_id && subtypes.length > 0) {
      const match = subtypes.find(s => s.subtype_id === Number(requestDetails.subtype_id));
      if (match) {
        setSelectedSubtype(Number(requestDetails.subtype_id));
      }
    }
  }, [subtypes, requestDetails]);

  // Set selectedPriority from requestDetails
  useEffect(() => {
    if (requestDetails?.priority && priorities.length > 0) {
      const match = priorities.find(p => p.priority_value === requestDetails.priority);
      if (match) {
        setSelectedPriority(match.priority_value);
      }
    }
  }, [priorities, requestDetails]);
  

  const handleUpdate = async (field, value) => {
    try {
      let updatedRequest = {
        ...requestDetails,
        request_type: Array.isArray(requestDetails.request_type)
          ? [requestDetails.request_type[0], requestDetails.request_type[1]]
          : requestDetails.request_type,
      };

      if (field === "request_type_id") {
        const selected = types.find(t => t.id === Number(value));
        updatedRequest.request_type = [selected?.id.toString(), selected?.type];
        updatedRequest.subtype_id = null;
      }

      if (field === "assigned_to") {
        try {
          await api.put(`/assignArchitecturalRequest`, {
            request_id: requestDetails.id,
            assign_to: value
          });
      
          setSelectedUser(value);
          const updated = { ...requestDetails, assigned_to: value };
          onUpdate(updated);
          setSnackbar({ open: true, message: "Assigned and email sent", severity: "success" });
        } catch (err) {
          console.error("Failed to assign and send email:", err);
          setSnackbar({ open: true, message: "Assigned, but email failed to send", severity: "warning" });
        }
        return;
      }
      
      
      
      
      if (field === "subtype_id") {
        updatedRequest.subtype_id = Number(value);
      }

      if (field === "priority_value") {
        updatedRequest.priority = value;
        const priorityObj = priorities.find(p => p.priority_value === value);
        updatedRequest.priority_color = priorityObj?.color_code || "#1976d2";
      }
      

      await api.put(`/architectural-requests/${requestDetails.id}`, updatedRequest);

      if (field === "request_type_id") {
        setSelectedType(Number(value));
        setSelectedSubtype("");
        onUpdate({ ...requestDetails, ...updatedRequest });
      } else if (field === "subtype_id") {
        setSelectedSubtype(Number(value));
        onUpdate({ ...requestDetails, ...updatedRequest });
      } else if (field === "priority_value") {
        setSelectedPriority(value);
        onUpdate({ ...requestDetails, ...updatedRequest });
      }

      setSnackbar({ open: true, message: "Request updated successfully", severity: "success" });
    } catch (err) {
      console.error(`Failed to update ${field}`, err);
      setSnackbar({ open: true, message: "Failed to update request", severity: "error" });
    }
  };

  return (
    <Box>
      <Box display="flex" justifyContent="flex-end" mb={2}>
        <Button
          variant="contained"
          onClick={() => editRequestRef.current?.openModal()}
          startIcon={<PendingActionsIcon />}
          sx={{
            textTransform: "none",
            fontWeight: 500,
            fontSize: "14px",
            borderRadius: "20px",
            px: 3,
            py: 1,
            backgroundColor: "#1976d2",
            color: "#ffffff",
            boxShadow: "0px 2px 6px rgba(25, 118, 210, 0.1)",
            "&:hover": {
              backgroundColor: "#63a4ff",
            }
          }}
        >
          Change Status
        </Button>
      </Box>

      <Box sx={{ textAlign: "center", mb: 2 }}>
  <Typography variant="subtitle2" sx={{ fontWeight: 500, color: "#666" }}>
    Request ID: <strong>REQ-{requestDetails?.id}</strong>
  </Typography>
</Box>


      <Grid container spacing={2}>
    

        {/* Request Type */}
        <Grid item xs={12}>
          <Card sx={{ p: 2 }}>
            <Box display="flex" alignItems="center" gap={2}>
              <InfoIcon color="primary" />
              <FormControl fullWidth size="small">
                <InputLabel>Request Type</InputLabel>
                <Select
                  label="Request Type"
                  value={selectedType || ""}
                  onChange={(e) => handleUpdate("request_type_id", e.target.value)}
                  disabled={loadingTypes}
                >
                  {types.map((type) => (
                    <MenuItem key={type.id} value={type.id}>
                      {type.type}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          </Card>
        </Grid>

        {/* Subtype */}
        <Grid item xs={12}>
          <Card sx={{ p: 2 }}>
            <Box display="flex" alignItems="center" gap={2}>
              <CategoryIcon color="info" />
              <FormControl fullWidth size="small">
                <InputLabel>Subtype</InputLabel>
                <Select
                  label="Subtype"
                  value={selectedSubtype || ""}
                  onChange={(e) => handleUpdate("subtype_id", e.target.value)}
                  disabled={!selectedType || loadingSubtypes}
                >
                  {subtypes.map((sub) => (
                    <MenuItem key={sub.subtype_id} value={sub.subtype_id}>
                      {sub.subtype_name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          </Card>
        </Grid>

        {/* Priority */}
        <Grid item xs={12}>
          <Card sx={{ p: 2 }}>
            <Box display="flex" alignItems="center" gap={2}>
              <PendingActionsIcon color="warning" />
              <FormControl fullWidth size="small">
                <InputLabel>Priority</InputLabel>
                <Select
                  label="Priority"
                  value={selectedPriority || ""}
                  onChange={(e) => handleUpdate("priority_value", e.target.value)}
                  disabled={loadingPriorities}
                >
                  {priorities.map((priority) => (
                    <MenuItem key={priority.priority_value} value={priority.priority_value}>
                      {priority.display_name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          </Card>
        </Grid>

        {/* Description */}
        <Grid item xs={12}>
          <Card sx={{ p: 2, display: "flex", alignItems: "center", gap: 2 }}>
            <DescriptionIcon color="secondary" />
            <Typography variant="body2">
              <strong>Description:</strong> {requestDetails?.description || "No Description Available"}
            </Typography>
          </Card>
        </Grid>

     {/* Assigned To */}
     <Grid item xs={12}>
  <Card sx={{ p: 2 }}>
    <Box display="flex" alignItems="center" gap={2}>
      <CheckCircleIcon color="primary" />
      <FormControl fullWidth size="small">
        <InputLabel>Assign To</InputLabel>
        <Select
          label="Assign To"
          value={selectedUser || ""}
          onChange={(e) => handleUpdate("assigned_to", e.target.value)}
        >
          {users.map(user => (
            <MenuItem key={user.UserID} value={user.UserID}>
              {user.Name} {user.Lastname} ({user.RoleName})
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    </Box>
  </Card>
</Grid>

        {/* Status */}
        <Grid item xs={12}>
          <Card sx={{ p: 2, display: "flex", alignItems: "center", gap: 2 }}>
            <CheckCircleIcon color={requestDetails?.status === "Approved" ? "success" : "error"} />
            <Typography variant="body2">
              <strong>Status:</strong> {requestDetails?.status || "N/A"}
            </Typography>
          </Card>
        </Grid>
        <Grid item xs={12}>
    <RequestFilesViewer requestId={requestDetails?.id} />
  </Grid>
      </Grid>

   


      <EditArchitecturalRequest
        ref={editRequestRef}
        architecturalRequestDetails={requestDetails}
        onUpdate={onUpdate}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default RequestDetailsPanel;
